import { ELEMENT_INFO, MARTIAL_ARTS } from '@nine/shared';
import {
  bell,
  blade,
  brush,
  chain,
  claw,
  cloud,
  drop,
  fist,
  flower,
  gust,
  lotus,
  moon,
  needle,
  palm,
  peak,
  sabre,
  scroll,
  snowflake,
  sprite,
  star,
  sword,
  taiji,
  inkStroke,
} from '../kit';
import { C, mix, type Pix } from '../pix';
import { defineIconKind, registerIcons } from '../store';

/**
 * Martial arts (วิชายุทธ์): a round medallion in the art's element, its emblem the art's weapon
 * and what sets it apart (nine swords fanned, a snowflake on the blade, a palm in cloud…).
 */

defineIconKind('art', {
  label: 'วิชายุทธ์',
  ids: () => Object.keys(MARTIAL_ARTS),
  name: (id) => MARTIAL_ARTS[id]?.name ?? id,
  tint: (id) => (MARTIAL_ARTS[id] ? ELEMENT_INFO[MARTIAL_ARTS[id].element].color : C.gold),
  plate: 'round',
  fallback: (id) => ({
    draw: (p, c) => {
      if (MARTIAL_ARTS[id]?.weapon === 'unarmed') fist(p, 10, 10, C.skin);
      else sword(p, 7, 25, 25, 7, { hilt: c.p.c });
    },
  }),
});

const qiFan = (p: Pix, n: number, col: string) => {
  for (let k = 0; k < n; k++) {
    const a = Math.PI + 0.35 + (k * (Math.PI - 0.7)) / (n - 1);
    p.line(
      16 + Math.cos(a) * 4,
      21 + Math.sin(a) * 4,
      16 + Math.cos(a) * 12,
      21 + Math.sin(a) * 13,
      k % 2 ? C.steel : col,
    );
    p.px(16 + Math.cos(a) * 12, 21 + Math.sin(a) * 13, C.white);
  }
};

registerIcons('art', {
  'nine-swords': (p) => {
    qiFan(p, 9, '#ffe07a');
    sword(p, 16, 28, 16, 9, { tassel: '#e0402a' });
  },
  'cloud-palm': (p) => {
    cloud(p, 5, 26, 22, C.paper);
    palm(p, 9, 6, C.skin, { lines: C.skinDark });
  },
  'breeze-sword': (p) => {
    sword(p, 7, 25, 25, 7, { hilt: C.jade, tassel: '#7af0c8' });
    gust(p, 4, 12, 10, '#c8fff0');
    gust(p, 15, 23, 10, '#c8fff0', false);
  },
  'drift-fist': (p) => {
    taiji(p, 16, 16, 10);
    fist(p, 15, 15, C.skin);
  },
  'drift-sword': (p) => {
    taiji(p, 16, 16, 10);
    sword(p, 6, 26, 26, 6, { hilt: '#c8c8d8' });
  },
  'snow-sword': (p) => {
    sword(p, 7, 25, 25, 7, { hilt: '#7ad0f0', blade: '#eef6ff' });
    snowflake(p, 10, 10, 5, C.white);
  },
  'bell-fist': (p) => {
    p.ring(16, 16, 11, 11, '#fff0a8');
    fist(p, 10, 10, '#ffcf5a', { shade: '#c8862a' });
  },
  'lamp-hook-sabre': (p) => {
    sabre(p, 7, 25, 25, 6, { hilt: C.gold, w: 3, curve: -2 });
    // A butterfly of iron at the guard.
    sprite(p, 3, 6, ['ss...ss', 'sss.sss', '.sssss.', '..sss..', '.ss.ss.'], { s: '#9aa6b8' });
  },
  'quiet-palm': (p) => {
    palm(p, 6, 7, '#e8b8f0', { lines: '#c070ff' });
    flower(p, 23, 22, 5, '#ff8ad0');
  },
  'ink-slate': (p) => {
    sword(p, 6, 26, 26, 6, { hilt: '#5cc8e8' });
    brush(p, 26, 26, 8, 8);
  },
  'heat-sabre': (p) => {
    sabre(p, 7, 25, 25, 6, { blade: '#ffb0a8', hilt: '#3a1a1a', w: 4 });
    drop(p, 22, 22, 3, C.blood);
  },
  'hum-palm': (p) => {
    lotus(p, 16, 26, 20, '#ffcf5a', C.paper);
    palm(p, 9, 3, '#ffcf5a', { lines: '#c8862a' });
  },
  'founder-sabre': (p) => {
    bell(p, 21, 9, 10, C.gold, { band: '#c8862a' });
    sword(p, 5, 27, 25, 7, { hilt: '#c89060' });
  },
  'chain-hold': (p) => {
    chain(p, 4, 24, 28, 24, C.steelMid);
    claw(p, 8, 5, C.skin, C.gold);
  },
  'quick-draw-sabre': (p) => {
    sabre(p, 7, 25, 25, 6, { hilt: C.gold, w: 3, curve: -2 });
    flower(p, 9, 10, 5, '#ffb0d8');
    flower(p, 23, 23, 3, '#ff8ad0');
  },
  'listening-blade': (p) => {
    moon(p, 12, 12, 8, '#f8f0c8');
    sword(p, 8, 27, 26, 9, { hilt: '#b07aff' });
  },
  'chorus': (p) => {
    for (const [pts, col] of [
      [
        [
          [4, 22],
          [10, 14],
          [16, 18],
          [22, 9],
          [28, 12],
        ],
        '#ff8ad0',
      ],
      [
        [
          [4, 12],
          [10, 20],
          [17, 13],
          [23, 22],
          [28, 18],
        ],
        '#c070ff',
      ],
    ] as const)
      inkStroke(p, pts, 4, col);
    flower(p, 16, 16, 4, '#ffb0d8');
  },
  'pulse-seal': (p) => {
    for (let k = 0; k < 6; k++) {
      const a = -Math.PI / 2 - 0.9 + (k * 1.8) / 5;
      p.line(
        16 + Math.cos(a) * 6,
        18 + Math.sin(a) * 6,
        16 + Math.cos(a) * 13,
        18 + Math.sin(a) * 13,
        k % 2 ? C.white : '#9ff0ff',
      );
    }
    sprite(p, 12, 16, ['..ss..', '..ss..', '.ssss.', 'ssssss', 'ssssss', '.ssss.'], { s: C.skin });
  },
  'one-cut': (p) => {
    scroll(p, 8, 5, 16, 20);
    peak(p, 15, 21, 10, 10, '#5a8aa8', null);
    peak(p, 20, 21, 8, 7, '#3a6a88', null);
    blade(p, 6, 28, 27, 7, { w: 2, col: C.steel });
  },
  'tongs-claw': (p) => {
    claw(p, 9, 6, '#e8a060', C.white);
    for (let k = 0; k < 3; k++) p.line(9 + k * 5, 18, 12 + k * 5, 27, C.blood);
  },
  'cliff-push-sabre': (p) => {
    p.poly(
      [
        [3, 28],
        [9, 10],
        [15, 16],
        [20, 6],
        [29, 28],
      ],
      '#c84a32',
    );
    p.rect(3, 26, 26, 3, mix(C.blood, C.ink, 0.2));
    sabre(p, 7, 25, 26, 5, { blade: '#ffb0a8', hilt: '#3a1a1a', w: 3 });
  },
  'dragon-palm': (p) => {
    inkStroke(
      p,
      [
        [4, 26],
        [9, 17],
        [6, 10],
        [14, 5],
        [24, 7],
      ],
      4,
      C.gold,
    );
    palm(p, 13, 12, C.skin, { lines: C.skinDark });
  },
  'hidden-arts': (p) => {
    for (let k = 0; k < 4; k++) {
      const a = -0.6 + k * 0.4;
      needle(p, 6, 26 - k * 3, 6 + Math.cos(a) * 20, 26 - k * 3 + Math.sin(a) * 10, C.steel);
    }
    sprite(p, 18, 4, ['...s...', '...s...', '..sss..', 'sssssss', '..sss..', '...s...', '...s...'], {
      s: C.steel,
    });
    star(p, 21, 7, 1, C.white);
  },
});
